"use strict";

import {Style, Fill, Stroke, Circle, RegularShape, Text} from "ol/style";
import Point from "ol/geom/Point";
import Polygon from "ol/geom/Polygon";
import LineString from "ol/geom/LineString";

/**
 * @classdesc
 * OL-Implementierung einer Factory zur Bereitstellung von Styles.
 *
 * @class
 * @extends gc.gis.factory.StyleFactory
 * @memberof gc.gis.factory
 * @author Christian Schmidt <c.schmidt@brain-scc.de>
 */
gc.gis.factory.OLStyleFactory = function()
{
	gc.gis.factory.StyleFactory.call(this, {});
};
gc.gis.factory.OLStyleFactory.prototype = Object.create(gc.gis.factory.StyleFactory.prototype);

/**
 * @inheritDoc
 */
gc.gis.factory.OLStyleFactory.prototype._init = function()
{
	const color_black = [0, 0, 0];
	const color_white = [255, 255, 255];
	const color_nodes = [0, 0, 255];
	const transparent = [255, 255, 255, 0.01];    // komplett Transparenz geht nicht, dann funktioniert die Selektion nicht mehr

	const color_draw = [255, 0, 0];
	const color_highlight_stroke = [170, 0, 0];
	const color_highlight_fill = [255, 215, 0];

	const txt_stroke = new Stroke({
		color: '#fff',
		width: 3
	});
	const txt_fill = new Fill({
		color: color_black
	});
	const txt_font = 'Calibri,sans-serif';
	const txt_font_bold = 'bold ' + txt_font;

	const txt_stroke_active = new Stroke({
		color: color_white,
		width: 5
	});
	const txt_fill_active = new Fill({
		color: color_draw
	});

	const draw_stroke = new Stroke({
		color: color_draw.concat(1),
		width: 2
	});
	const draw_dashed = new Stroke({
		color: color_draw.concat(1),
		width: 2,
		lineDash: [10, 10]
	});
	const draw_fill = new Fill({
		color: color_white.concat(0.5)
	});
	const draw_fill_final = new Fill({
		color: color_draw.concat(0.3)
	});
	const draw_image = new Circle({
		radius: 4,
		fill: draw_fill_final,
		stroke: draw_stroke
	});

	// Transparent-Style
	const transparentFill = new Fill({
		color: transparent
	});
	const transparentStroke = new Stroke({
		color: transparent,
		width: 4
	});
	const transparentImage = new Circle({
		radius: 6,
		fill: transparentFill,
		stroke: transparentStroke
	});
	this._style[gc.gis.factory.StyleType.TRANSPARENT] = new Style({
		fill: transparentFill,
		stroke: transparentStroke,
		image: transparentImage
	});

	// Markierung von Stützpunkten
	const nodeMarker = function (geometry, styles, final)
	{
		if (geometry && geometry.getType() !== 'Point')
		{
			const stroke = new Stroke({
				color: color_nodes.concat(1.0),
				width: 2
			});

			// siehe https://openlayers.org/en/latest/examples/regularshape.html
			const x = new RegularShape({
				stroke: stroke,
				points: 4,
				radius: 6,
				radius2: 0,
				angle: Math.PI / 4
			});

			// Trick: Transparenten Style setzen, damit beim Hover über Stützpunkte auch das Feature gefunden wird
			const r = new Circle({
				radius: 10,
				fill: transparentFill
			});

			//var coords = geometry.getFlatCoordinates();
			const coords = gc.gis.OLUtil.getFlatCoordinates(geometry);
			let cnt = coords.length - 1;
			if (!final)
			{
				cnt -= (geometry.getType() === 'Polygon' ? 4 : 2);
			}

			for (let i=0; i < cnt; i++)
			{
				let props = {
					geometry: new Point([coords[i], coords[i+1]]),
					image: x
				};
				styles.push(new Style(props));
				props = {
					geometry: new Point([coords[i], coords[i+1]]),
					image: r
				};
				styles.push(new Style(props));
			}
		}
	};

	// Hightlight-Style
	let hightlightFill = new Fill({
		color: color_highlight_fill.concat(0.4)
	});
	let hightlightStroke = new Stroke({
		color: color_highlight_stroke,
		width: 2
	});
	let hightlightImage = new Circle({
		radius: 6,
		fill: hightlightFill,
		stroke: hightlightStroke
	});
	this._style[gc.gis.factory.StyleType.HIGHLIGHT] = new Style({
		fill: hightlightFill,
		stroke: hightlightStroke,
		image: hightlightImage
	});
	hightlightFill = new Fill({
		color: color_highlight_fill.concat(0.75)
	});
	hightlightStroke = new Stroke({
		color: color_highlight_stroke,
		width: 4
	});
	hightlightImage = new Circle({
		radius: 10,
		fill: hightlightFill,
		stroke: hightlightStroke
	});
	this._style[gc.gis.factory.StyleType.HIGHLIGHT_ACTIVE] = new Style({
		fill: hightlightFill,
		stroke: hightlightStroke,
		image: hightlightImage
	});

	// selektierbares Feature (Hover)
	const selectableFill = new Fill({
		color: color_draw.concat(0.15)
	});
	const selectableStroke = new Stroke({
		color: color_draw.concat(0.5),
		width: 4
	});
	const selectableImage = new Circle({
		radius: 6,
		fill: selectableFill,
		stroke: selectableStroke
	});
	this._style[gc.gis.factory.StyleType.SELECTABLE] = new Style({
		fill: selectableFill,
		stroke: selectableStroke,
		image: selectableImage
	});

	// selektiertes Feature
	const selectFill = new Fill({
		color: color_draw.concat(0.5)
	});
	const selectStroke = new Stroke({
		color: color_draw.concat(1),
		width: 2
	});
	const selectImage = new Circle({
		radius: 6,
		fill: selectFill,
		stroke: selectStroke
	});
	const selectStyle = new Style({
		fill: selectFill,
		stroke: selectStroke,
		image: selectImage
	});

	this._styleFunction[gc.gis.factory.StyleType.SELECTED] = function(feature, resolution)
	{
		const styles = [];
		styles.push(selectStyle);
		nodeMarker(feature.getGeometry(), styles, true);

		return styles;
	};

	// geocodiertes Feature
	let geocodedFill = new Fill({
		color: color_white.concat(0.3)
	});
	let geocodedStroke = new Stroke({
		color: color_highlight_stroke,
		width: 2
	});
	let geocodedImage = new Circle({
		radius: 6,
		fill: geocodedFill,
		stroke: geocodedStroke
	});
	this._style[gc.gis.factory.StyleType.GEOCODE] = new Style({
		fill: geocodedFill,
		stroke: geocodedStroke,
		image: geocodedImage
	});
	geocodedFill = new Fill({
		color: color_white.concat(0.75)
	});
	geocodedStroke = new Stroke({
		color: color_highlight_stroke,
		width: 4
	});
	geocodedImage = new Circle({
		radius: 10,
		fill: geocodedFill,
		stroke: geocodedStroke
	});
	this._style[gc.gis.factory.StyleType.GEOCODE_ACTIVE] = new Style({
		fill: geocodedFill,
		stroke: geocodedStroke,
		image: geocodedImage
	});

	// Messfunktion
	this._styleFunction[gc.gis.factory.StyleType.MEASURE] = function(feature, resolution)
	{
		const ol_map = gc.gis.iface.map.getMap().getImpl(),
			geom = feature.getGeometry(),
			polygonMeasure = feature.get('isSnappedOnFirst') || false;
		let styles = [],
			sketch,
			props,
			coords,
			i, ii;

		if (geom instanceof Polygon)
		{
			// Ol-Draw interaction generates automatically a sketchLine when drawing  polygon
			sketch = new Style({fill: polygonMeasure ? draw_fill_final : draw_fill});
			styles.push(sketch);

			if (polygonMeasure)
			{
				// Flächeninhalt anzeigen
				const area = gc.gis.GCUtil.formatArea(gc.gis.OLUtil.calcArea(geom.getCoordinates()[0],
					ol_map.getView().getProjection()));
				if(area.value > 0.0)
				{
					props = {
						text: new Text({
							font:   '14px ' + txt_font,
							text:   area.formattedValue + ' ' + area.unit,
							fill:   txt_fill,
							stroke: txt_stroke
						})
					};

					styles.push(new Style(props));
				}
			}
		}
		else if (geom instanceof Point)
		{
			sketch = new Style({image: draw_image});
			styles.push(sketch);
		}
		else // LineString
		{
			coords = geom.getCoordinates();
			styles = [];
			for (i = 0, ii = coords.length - 1; i < ii; i++)
			{
				const start = coords[i];
				const end = coords[i+1];
				const length = gc.gis.GCUtil.formatLength(
					gc.gis.OLUtil.calcLength(start, end, ol_map.getView().getProjection()));

				props = {
					geometry: new LineString([start, end]),
					stroke: i < coords.length - 2 ? draw_stroke : draw_dashed,    // nur das aktuelle Zeichen-Segment gestrichelt
					text: new Text({
						font:   '12px ' + txt_font,
						text:   length.formattedValue + ' ' + length.unit,
						fill:   txt_fill,
						stroke: txt_stroke
					})
				};

				styles.push(new Style(props));
			}
		}
		return styles;
	};

	this._styleFunction[gc.gis.factory.StyleType.MEASURE_FINAL] = function(feature, resolution) {
		const ol_map = gc.gis.iface.map.getMap().getImpl();
		let styles,
			props,
			geom;

		props = {
			fill: draw_fill_final,
			stroke: draw_stroke
		};

		styles = [new Style(props)];

		geom = feature.getGeometry();
		if (geom instanceof Polygon)
		{
			// Flächeninhalt anzeigen
			const area = gc.gis.GCUtil.formatArea(gc.gis.OLUtil.calcArea(geom.getCoordinates()[0],
				ol_map.getView().getProjection()));
			if(area.value > 0.0)
			{
				props = {
					text: new Text({
						font:   '14px ' + txt_font,
						text:   area.formattedValue + ' ' + area.unit,
						fill:   txt_fill,
						stroke: txt_stroke
					})
				};

				styles.push(new Style(props));
			}

			// Linie erstellen, um Segmentlängen anzuzeigen
			geom = new LineString(geom.getCoordinates()[0]);
		}

		// Segmentlängen anzeigen
		if (geom instanceof LineString)
		{
			geom.forEachSegment(function (start, end) {
				const length = gc.gis.GCUtil.formatLength(gc.gis.OLUtil.calcLength(start, end,
					ol_map.getView().getProjection()));

				props = {
					geometry: new LineString([start, end]),
					text: new Text({
						font:   '12px ' + txt_font,
						text:   length.formattedValue + ' ' + length.unit,
						fill:   txt_fill,
						stroke: txt_stroke
					})
				};

				styles.push(new Style(props));
			});
		}

		return styles;
	};
	const measure_stroke_active = new Stroke({
		color:    '#ff0',
		width:    3,
		lineDash: [10, 10]
	});
	this._styleFunction[gc.gis.factory.StyleType.MEASURE_ACTIVE] = function(feature, resolution)
	{
		return [new Style({
			stroke: measure_stroke_active,
			text: new Text({
				font:   '12px ' + txt_font_bold,
				text:   feature.get('text') || 'ERROR',
				fill:   txt_fill_active,
				stroke: txt_stroke_active
			})
		})];
	};


	// Erfassungsfunktion
	this._styleFunction[gc.gis.factory.StyleType.DRAW] = function(feature, resolution)
	{
		const geom = feature.getGeometry(),
			isClosed = feature.get('isSnappedOnFirst') || false,
			geometryType = feature.get('finalGeometryType');
		let styles = [],
			props,
			sketch;

		if (geom instanceof Polygon)
		{
			// ol-Draw interaction generates automatically a sketchLine when drawing  polygon
			sketch = new Style({fill: isClosed ? draw_fill_final : draw_fill});
			styles.push(sketch);
		}
		else if (geom instanceof Point)
		{
			sketch = new Style({image: draw_image});
			styles.push(sketch);
		}
		else // LineString
		{
			const coords = geom.getCoordinates();
			const semiFinal = (geometryType === gc.gis.geom.GCGeometryType.POLYGON
				|| geometryType === gc.gis.geom.GCGeometryType.LINESTRING);

			styles = [];
			if (!semiFinal)
			{
				styles.push(new Style({stroke: draw_stroke}));
			}
			else
			{
				for (let i=0; i < coords.length - 1; i++)
				{
					const start = coords[i];
					const end = coords[i+1];
					props = {
						geometry: new LineString([start, end]),
						stroke: i < coords.length - 2 ? draw_stroke : draw_dashed    // nur das aktuelle Zeichen-Segment gestrichelt
					};

					styles.push(new Style(props));
				}
			}
		}

		nodeMarker(geom, styles, false);

		return styles;
	};

	this._styleFunction[gc.gis.factory.StyleType.DRAW_FINAL] = function(feature, resolution)
	{
		const styles = [];

		styles.push(new Style({
			fill: draw_fill_final,
			stroke: draw_stroke,
			image: draw_image
		}));

		if (feature.get('selected'))
		{
			nodeMarker(feature.getGeometry(), styles, true);
		}

		return styles;
	};


	// WFS Features
	const wfsFill = new Fill({
		color: color_white.concat(0.4)
	});
	const wfsStroke = new Stroke({
		color: [51, 122, 183],
		width: 2
	});
	const wfsImage = new Circle({
		radius: 6,
		fill: wfsFill,
		stroke: wfsStroke
	});
	this._style[gc.gis.factory.StyleType.WFS] = new Style({
		fill: wfsFill,
		stroke: wfsStroke,
		image: wfsImage
	});
};
